import { getApiBaseUrl } from '../lib/api';
import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  Camera, Package, Truck, CheckCircle, Clock, 
  AlertTriangle, ChevronRight, MapPin, Calendar,
  ArrowRight, RefreshCw
} from 'lucide-react';

const API_URL = getApiBaseUrl();
const getToken = () => localStorage.getItem('access_token') || localStorage.getItem('token');

// Static color class maps for Tailwind compilation
const statusBgColorMap = {
  yellow: 'bg-yellow-100',
  blue: 'bg-blue-100',
  green: 'bg-green-100',
  purple: 'bg-purple-100',
  red: 'bg-red-100',
};

const statusTextColorMap = {
  yellow: 'text-yellow-800',
  blue: 'text-blue-800',
  green: 'text-green-800',
  purple: 'text-purple-800',
  red: 'text-red-800',
};

const STATUS_CONFIG = {
  pending: { color: 'yellow', icon: Clock, label: 'Awaiting Kit Order' },
  kit_ordered: { color: 'blue', icon: Truck, label: 'Kit Ordered' },
  kit_delivered: { color: 'green', icon: Package, label: 'Kit Delivered' },
  in_progress: { color: 'purple', icon: Camera, label: 'In Progress' },
  completed: { color: 'green', icon: CheckCircle, label: 'Completed' },
  processing: { color: 'blue', icon: RefreshCw, label: 'Processing' },
  report_ready: { color: 'green', icon: CheckCircle, label: 'Report Ready' },
  overdue: { color: 'red', icon: AlertTriangle, label: 'Overdue' }
};

export default function TenantInspections() {
  const [cases, setCases] = useState([]);
  const [selectedCase, setSelectedCase] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchCases();
  }, []);

  const fetchCases = async () => {
    try {
      const response = await fetch(`${API_URL}/api/tenant/inspections/cases`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCases(data.cases || []);
        if (data.cases?.length > 0 && !selectedCase) {
          fetchCaseDetails(data.cases[0].id);
        }
      }
    } catch (error) {
      console.error('Failed to fetch cases:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCaseDetails = async (caseId) => {
    try {
      const response = await fetch(`${API_URL}/api/tenant/inspections/cases/${caseId}`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSelectedCase(data);
      }
    } catch (error) {
      console.error('Failed to fetch case details:', error);
    }
  };

  const performAction = async (action) => {
    if (!selectedCase) return;
    setActionLoading(true);
    
    try {
      let endpoint = '';
      let body = {};
      
      switch (action) {
        case 'order_kit':
          endpoint = `/api/tenant/inspections/cases/${selectedCase.id}/order-kit`;
          break;
        case 'confirm_delivery':
          endpoint = `/api/tenant/inspections/cases/${selectedCase.id}/confirm-delivery`;
          break;
        case 'start_inspection':
          endpoint = `/api/tenant/inspections/cases/${selectedCase.id}/start-inspection`;
          break;
        case 'complete':
          endpoint = `/api/tenant/inspections/cases/${selectedCase.id}/complete`;
          break;
        default:
          return;
      }
      
      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });
      
      if (response.ok) {
        toast.success('Action completed successfully');
        fetchCases();
        fetchCaseDetails(selectedCase.id);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Action failed');
      }
    } catch (error) {
      toast.error('Failed to perform action');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusBgColorMap[config.color] || 'bg-yellow-100'} ${statusTextColorMap[config.color] || 'text-yellow-800'}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Property Inspections</h1>
          <p className="text-gray-600">Complete your property inspections using our LiDAR scanning kit</p>
        </div>

        {cases.length === 0 ? (
          <Card className="p-8 text-center">
            <Camera className="w-12 h-12 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium mb-2">No Inspection Cases</h3>
            <p className="text-gray-500">You don&apos;t have any pending inspections at the moment.</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Cases List */}
            <div className="lg:col-span-1 space-y-3">
              <h2 className="font-bold text-lg mb-4">Your Inspections</h2>
              {cases.map((c) => (
                <Card
                  key={c.id}
                  className={`p-4 cursor-pointer transition-all ${
                    selectedCase?.id === c.id ? 'ring-2 ring-primary' : 'hover:shadow-md'
                  }`}
                  onClick={() => fetchCaseDetails(c.id)}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-medium text-sm">{c.inspection_type?.replace('_', ' ').toUpperCase()}</p>
                      <p className="text-xs text-gray-500">{c.property_address}</p>
                    </div>
                    {getStatusBadge(c.status)}
                  </div>
                  {c.days_remaining !== null && (
                    <p className={`text-xs ${c.is_overdue ? 'text-red-600' : 'text-gray-500'}`}>
                      {c.is_overdue ? `${Math.abs(c.days_remaining)} days overdue` : `${c.days_remaining} days remaining`}
                    </p>
                  )}
                </Card>
              ))}
            </div>

            {/* Case Details */}
            <div className="lg:col-span-2">
              {selectedCase ? (
                <Card className="p-6">
                  <div className="flex items-start justify-between mb-6">
                    <div>
                      <h2 className="text-xl font-bold">
                        {selectedCase.inspection_type?.replace('_', ' ').toUpperCase()} Inspection
                      </h2>
                      <p className="text-gray-600 flex items-center gap-1 mt-1">
                        <MapPin className="w-4 h-4" />
                        {selectedCase.property?.address?.line1}, {selectedCase.property?.address?.city}
                      </p>
                    </div>
                    {getStatusBadge(selectedCase.status)}
                  </div>

                  {/* Status Message */}
                  <div className={`p-4 rounded-lg mb-6 ${
                    selectedCase.is_overdue ? 'bg-red-50 border border-red-200' : 'bg-blue-50 border border-blue-200'
                  }`}>
                    <p className={`font-medium ${selectedCase.is_overdue ? 'text-red-800' : 'text-blue-800'}`}>
                      {selectedCase.status_message}
                    </p>
                    {selectedCase.days_remaining !== null && (
                      <p className={`text-sm mt-1 ${selectedCase.is_overdue ? 'text-red-700' : 'text-blue-700'}`}>
                        {selectedCase.is_overdue 
                          ? `This inspection is ${Math.abs(selectedCase.days_remaining)} days overdue`
                          : `You have ${selectedCase.days_remaining} days to complete this step`
                        }
                      </p>
                    )}
                  </div>

                  {/* Timeline */}
                  <div className="mb-6">
                    <h3 className="font-medium mb-4">Inspection Timeline</h3>
                    <div className="space-y-4">
                      <TimelineStep 
                        label="Case Created" 
                        date={selectedCase.created_at}
                        completed={true}
                      />
                      <TimelineStep 
                        label="Kit Ordered" 
                        date={selectedCase.kit_ordered_date}
                        completed={!!selectedCase.kit_ordered_date}
                        active={selectedCase.status === 'pending'}
                      />
                      <TimelineStep 
                        label="Kit Delivered" 
                        date={selectedCase.kit_delivered_date}
                        completed={!!selectedCase.kit_delivered_date}
                        active={selectedCase.status === 'kit_ordered'}
                      />
                      <TimelineStep 
                        label="Inspection Started" 
                        date={selectedCase.started_date}
                        completed={!!selectedCase.started_date}
                        active={selectedCase.status === 'kit_delivered'}
                      />
                      <TimelineStep 
                        label="Inspection Completed" 
                        date={selectedCase.completed_date}
                        completed={!!selectedCase.completed_date}
                        active={selectedCase.status === 'in_progress'}
                      />
                      <TimelineStep 
                        label="Report Ready" 
                        date={null}
                        completed={selectedCase.status === 'report_ready'}
                        active={selectedCase.status === 'completed' || selectedCase.status === 'processing'}
                      />
                    </div>
                  </div>

                  {/* Action Button */}
                  {selectedCase.next_action?.action && (
                    <div className="mt-6">
                      <Button
                        onClick={() => performAction(selectedCase.next_action.action)}
                        disabled={actionLoading}
                        className="w-full"
                        size="lg"
                      >
                        {actionLoading ? (
                          <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                        ) : (
                          <ArrowRight className="w-5 h-5 mr-2" />
                        )}
                        {selectedCase.next_action.label}
                      </Button>
                      <p className="text-sm text-gray-500 text-center mt-2">
                        {selectedCase.next_action.description}
                      </p>
                    </div>
                  )}

                  {/* Instructions */}
                  {selectedCase.status === 'kit_delivered' && (
                    <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                      <h4 className="font-medium mb-2">How to complete your inspection:</h4>
                      <ol className="list-decimal list-inside space-y-2 text-sm text-gray-600">
                        <li>Open the inspection kit and follow the setup guide</li>
                        <li>Use the LiDAR scanner to scan each room</li>
                        <li>Take photos of any existing damage or issues</li>
                        <li>Complete the digital checklist in the app</li>
                        <li>Pack everything back into the box and use the return label</li>
                      </ol>
                    </div>
                  )}
                </Card>
              ) : (
                <Card className="p-8 text-center">
                  <p className="text-gray-500">Select an inspection case to view details</p>
                </Card>
              )}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

function TimelineStep({ label, date, completed, active }) {
  return (
    <div className="flex items-center gap-3">
      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
        completed 
          ? 'bg-green-500 text-white' 
          : active 
            ? 'bg-blue-500 text-white animate-pulse'
            : 'bg-gray-200 text-gray-400'
      }`}>
        {completed ? <CheckCircle className="w-4 h-4" /> : <div className="w-2 h-2 rounded-full bg-current" />}
      </div>
      <div className="flex-1">
        <p className={`font-medium ${completed ? 'text-gray-900' : 'text-gray-400'}`}>{label}</p>
        {date && (
          <p className="text-xs text-gray-500">
            {new Date(date).toLocaleDateString('en-GB', { 
              day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' 
            })}
          </p>
        )}
      </div>
    </div>
  );
}
